<script>
	// ╔═══════════════════════════════════════════════════════════════════════╗
	// ║ LINKED EXPECTATION — must stay in lockstep with ConceptFull.svelte     ║
	// ║                                                                        ║
	// ║ ConceptFull is the "open full" / new-tab variant of the same concept   ║
	// ║ rendering. Both views must produce the SAME tree structure, the SAME   ║
	// ║ flatten semantics (trunk → branch → leaves), the SAME generality       ║
	// ║ formatting, tier classes, and domain shortening.                       ║
	// ║                                                                        ║
	// ║ When you change leavesOf, childrenOf, formatGenerality, tierClass,     ║
	// ║ tierLabel, or shortDomain here, you MUST mirror the change in          ║
	// ║ /src/routes/ConceptFull.svelte (and vice versa).                       ║
	// ╚═══════════════════════════════════════════════════════════════════════╝
	import { onMount, afterUpdate } from 'svelte';
	import { fetchConceptTree, fetchSystemConcepts, fetchSystemConcept, emailConceptSnapshot, trashConceptNodes } from '../lib/api.js';
	import { authState } from '../lib/auth.js';
	import OrientationBlock from '../components/OrientationBlock.svelte';
	import CorrectModal from '../components/CorrectModal.svelte';

	// Tenant display name for the lane-1 schema header that holds all
	// tenant content. Falls back to "Workspace" when authState hasn't
	// resolved yet (rare — Explorer renders only after auth completes).
	$: tenantDisplayName = $authState?.tenant?.name || 'Workspace';

	// Synthetic domain slug that clusters every system concept into a single
	// group at the top of the roots lane. Kept out of the regular domain
	// sort so "ADRASI System" always reads first regardless of concept count.
	const SYSTEM_DOMAIN_KEY = '__adrasi_system__';
	import Navbar from '../components/Navbar.svelte';
	import { X, CircleEllipsis } from 'lucide-svelte';

	export let navigate;

	// ── Tree state ──
	let allNodes = [];
	let domains = [];
	let activeDomain = null;
	let loading = true;

	// Lane selections
	let selectedRootId = null;
	// Collapsed domain groups in the roots lane. Key is the domain slug
	// (or '__unscoped__'). Svelte 3 needs reassignment for reactivity on
	// Set mutations, hence the `= collapsedGroups` dance.
	let collapsedGroups = new Set();
	// Roots-lane sort axis: 'alphabetical' = A→Z by concept name (default, scan
	// by name); 'date' = most-recently-active subtree first (using the same
	// subtreeMaxUpdatedAt signal the age column already shows — sort matches
	// what the eye is reading); 'generality' = most-general first (tier-ordered
	// browsing). All modes tie-break by alphabetical so order stays deterministic.
	// Default 'alphabetical' 2026-04-26; 'date' mode added 2026-05-31. Cycle
	// order is discovery-oriented first (known-name → recent activity → tier).
	let rootsSortMode = 'alphabetical';
	function toggleRootsSort() {
		const next = { alphabetical: 'date', date: 'generality', generality: 'alphabetical' };
		rootsSortMode = next[rootsSortMode];
	}

	// Button presentation lookup — three-character labels match the existing
	// ABC / .99 footprint; titles describe both the current state and the
	// next state on click for self-documenting affordance.
	$: sortMeta = {
		alphabetical: { label: 'ABC', title: 'Sorted alphabetically — tap for newest first', aria: 'Sort by date' },
		date:         { label: 'NEW', title: 'Sorted by newest first — tap for generality', aria: 'Sort by generality' },
		generality:   { label: '.99', title: 'Sorted by generality — tap for alphabetical', aria: 'Sort alphabetically' }
	}[rootsSortMode];
	// Outline lane: collapse leaves to show only branch-level nodes so the reader
	// can see the shape of the selected root at a glance. Branches are direct
	// children of selectedRoot; leaves are every descendant below the branch
	// (any substrate depth) returned flat by leavesOf().
	let outlineLeavesCollapsed = false;
	function toggleOutlineLeaves() {
		outlineLeavesCollapsed = !outlineLeavesCollapsed;
	}

	// Lane 3 (outline) collapsible state — collapses to a vertical rail when
	// the user wants more screen for lane 2's concept content. Default true
	// (collapsed) since outline-as-navigation is a power-user feature most
	// readers don't need most of the time. Persisted to localStorage so the
	// user's choice survives reloads.
	// Key versioned (v2) — bumping invalidates stale 'false' values from users
	// who opened the outline before it became default-collapsed.
	const OUTLINE_LANE_KEY = 'explorer.outline_lane_collapsed.v2';
	let outlineLaneCollapsed = (typeof localStorage !== 'undefined'
		&& localStorage.getItem(OUTLINE_LANE_KEY) !== 'false');
	function toggleOutlineLane() {
		outlineLaneCollapsed = !outlineLaneCollapsed;
		if (typeof localStorage !== 'undefined') {
			localStorage.setItem(OUTLINE_LANE_KEY, String(outlineLaneCollapsed));
		}
	}
	function toggleGroup(key) {
		if (collapsedGroups.has(key)) collapsedGroups.delete(key);
		else collapsedGroups.add(key);
		collapsedGroups = collapsedGroups;
	}
	function toggleAllGroups() {
		if (allGroupsCollapsed) {
			collapsedGroups = new Set();
		} else {
			collapsedGroups = new Set(rootGroups.map(g => g.domain || '__unscoped__'));
		}
	}
	let selectedNodeId = null;

	// Detail scroll container
	let conceptScrollEl = null;

	// ── Sub-navbar filters ──
	// Root lane shows trunk-level nodes (generality ≥ 0.65) at the top, then
	// orphan branches (0.35-0.65) that have no parent trunk, rendered in a
	// lighter style. No generality slider — the split is fixed by tier.
	const TRUNK_THRESHOLD = 0.65;
	const BRANCH_THRESHOLD = 0.35;

	// Recency: 0 = now, 1 = forever. Non-linear curve wired in a later pass.
	let recency = 1;

	// Search
	let searchQuery = '';

	// Modal
	let modal = null;
	let showMetadata = false;

	// Overflow menu
	let showOverflow = false;

	// Correction modal — opened from the overflow menu's "Correct or update"
	// item. Keyed to selectedRoot at the moment the user clicks. On success
	// we refetch the tree so the corrected concept reflects the new state.
	let correctModalOpen = false;
	let correctModalConcept = null;
	function openCorrectModal() {
		if (!selectedRoot) return;
		correctModalConcept = selectedRoot;
		correctModalOpen = true;
		showOverflow = false;
	}
	function closeCorrectModal() {
		correctModalOpen = false;
		correctModalConcept = null;
	}
	async function onCorrectSuccess() {
		// Refetch so the lane-2 detail reflects the correction immediately
		try {
			const data = await fetchConceptTree();
			if (data && Array.isArray(data.nodes)) allNodes = data.nodes;
		} catch (_e) { /* refresh failure is non-fatal */ }
	}

	// ── Mobile ──
	let mobileLane = 'roots'; // 'roots' | 'concept' | 'outline'
	let mobileFiltersOpen = false;
	let isMobile = false;

	function checkMobile() {
		isMobile = window.innerWidth <= 768;
	}

	// Prune mode
	let pruneMode = false;
	let pruneSelected = new Set();
	let pruneHolding = false;
	let pruneHoldTimer = null;
	let pruneHoldProgress = 0;
	let pruneHoldInterval = null;
	let pruning = false;

	// =========================================================================
	// Data loading
	// =========================================================================

	// Map a system_concepts row into the tenant-node shape so the rest of the
	// Explorer renders it uniformly. `_system: true` is a hook for styling;
	// `_slug` retains the registry key so the detail pane can re-fetch the
	// full content payload on demand (list endpoint returns metadata only).
	//
	// Tree-aware: respects the API's parent_id, depth, and generality so
	// hierarchical system docs (e.g. system.overview tree authored
	// 2026-05-03) render with their real branch/leaf structure rather than
	// being flattened to all-roots. Legacy flat docs (gardener.rubric, etc.)
	// have parent_id=NULL and depth=0 from the API, so they continue to
	// render as roots and pick up H2-section pseudo-children downstream.
	//
	// `_systemParentId` carries the raw API parent_id so the second pass can
	// resolve it to the corresponding pseudo-id (`sys-` + uuid) after every
	// system row has been mapped.
	function toSystemPseudoNode(sc) {
		const isOverview = sc.slug === 'adrasi.overview' || /\.overview$/.test(sc.slug || '');
		const apiGenerality = typeof sc.generality === 'string' ? parseFloat(sc.generality) : sc.generality;
		return {
			id: 'sys-' + sc.id,
			_system: true,
			_slug: sc.slug,
			_systemId: sc.id,
			_systemParentId: sc.parent_id || null,
			parent_id: null,                          // resolved in second pass
			concept: sc.concept,
			context: sc.context || '',
			content: sc.content || {},
			entities: sc.entities || [],
			domains: [SYSTEM_DOMAIN_KEY],
			// Use the API's generality when present (new tree-shaped docs
			// carry their own gen values: 0.98 root, 0.55-0.70 branch, 0.30
			// leaf). Fall back to legacy hardcode for rows that pre-date the
			// tree migration (gen still 0.98 but isOverview gets 0.99 to
			// keep system.overview reading above sibling rubrics).
			generality: typeof apiGenerality === 'number' && !Number.isNaN(apiGenerality)
				? apiGenerality
				: (isOverview ? 0.99 : 0.98),
			depth: typeof sc.depth === 'number' ? sc.depth : 0,
			node_type: sc.node_type || 'system_doc',
			updated_at: sc.updated_at || sc.seeded_at,
			created_at: sc.created_at || sc.seeded_at,
		};
	}

	// Parse the H2 sections out of a rubric's markdown and emit a child
	// pseudo-node for each one. Result: system docs render with a clickable
	// children list and inline sections — identical to how a tenant concept
	// with children renders. Generality tiers down from 0.98 (rubric root)
	// into the 0.55–0.75 topic-trunk band so the tier label reads correctly.
	function parseSystemSections(parentNode, markdown) {
		if (!markdown || typeof markdown !== 'string') return [];
		// Match H2 (## ) headings and the body that follows up to the next H2.
		const lines = markdown.split(/\r?\n/);
		const sections = [];
		let current = null;
		for (const line of lines) {
			const m = line.match(/^##\s+(.+?)\s*$/);
			if (m) {
				if (current) sections.push(current);
				current = { heading: m[1], body: [] };
			} else if (current) {
				current.body.push(line);
			}
		}
		if (current) sections.push(current);

		return sections.map((s, i) => ({
			id: parentNode.id + '-s' + i,
			_system: true,
			_systemSection: true,
			parent_id: parentNode.id,
			// Strip any leading numbering ("1. Naming Law" → "Naming Law")
			// so the child reads like a concept name rather than a doc heading.
			concept: s.heading.replace(/^\d+\.\s*/, ''),
			context: '',
			content: { markdown: s.body.join('\n').trim() },
			entities: [],
			domains: [SYSTEM_DOMAIN_KEY],
			generality: 0.7,
			depth: 1,
			node_type: 'system_section',
			updated_at: parentNode.updated_at,
			created_at: parentNode.created_at,
		}));
	}

	async function loadTree() {
		try {
			const [tree, systemResp] = await Promise.all([
				fetchConceptTree(),
				fetchSystemConcepts().catch(err => {
					console.warn('system concepts unavailable:', err?.message || err);
					return { system_concepts: [] };
				}),
			]);
			const tenantNodes = (tree.nodes || []).map(n => ({
				...n,
				generality: typeof n.generality === 'string' ? parseFloat(n.generality) : n.generality
			}));

			// Hydrate every system doc up front — there are only a handful, and
			// fetching them eagerly lets us materialize their H2 sections as
			// first-class children. That way the roots-lane descendant count,
			// the children TOC, and the inline section rendering all work
			// identically to tenant concepts.
			const systemMeta = systemResp.system_concepts || [];
			const hydrated = await Promise.all(systemMeta.map(async sc => {
				try {
					const full = await fetchSystemConcept(sc.slug);
					return full || sc;
				} catch {
					return sc;
				}
			}));

			// First pass: map every system row to a pseudo-node with placeholder
			// parent_id (the raw API parent uuid is parked on _systemParentId).
			const systemNodes = hydrated.map(toSystemPseudoNode);

			// Second pass: resolve _systemParentId (a UUID into system_concepts)
			// to the matching pseudo-id (`sys-` + uuid). Nodes whose API parent
			// is null stay as roots; nodes whose parent isn't in the hydrated
			// set keep parent_id=null defensively (shouldn't happen in healthy
			// state, surfaces the orphan visibly when it does).
			const idToPseudoId = new Map();
			for (const n of systemNodes) {
				if (n._systemId) idToPseudoId.set(n._systemId, n.id);
			}
			for (const n of systemNodes) {
				n.parent_id = n._systemParentId
					? (idToPseudoId.get(n._systemParentId) || null)
					: null;
			}

			// H2-section pseudo-children: only synthesized for nodes that have
			// NO actual children in the substrate. This preserves the legacy
			// flat-doc rendering (gardener.rubric → its H2 sections become
			// clickable kids) without double-rendering for tree-shaped docs
			// (system.overview branches/leaves carry their own real children).
			const childCountByParent = new Map();
			for (const n of systemNodes) {
				if (n.parent_id) {
					childCountByParent.set(n.parent_id, (childCountByParent.get(n.parent_id) || 0) + 1);
				}
			}
			const systemSections = systemNodes.flatMap(node => {
				if ((childCountByParent.get(node.id) || 0) > 0) return [];
				return parseSystemSections(node, node.content?.markdown || '');
			});

			allNodes = [...systemNodes, ...systemSections, ...tenantNodes];
			const s = new Set();
			tenantNodes.forEach(n => (n.domains || []).forEach(d => s.add(d)));
			domains = [...s].sort();
		} catch (err) {
			console.error('Failed to load concept tree:', err);
		} finally {
			loading = false;
		}
	}

	async function refreshAll() {
		loading = true;
		selectedRootId = null;
		selectedNodeId = null;
		await loadTree();
	}

	// =========================================================================
	// Tree helpers
	// =========================================================================

	function childrenOf(parentId) {
		return allNodes.filter(n => n.parent_id === parentId);
	}

	function descendantCount(nodeId) {
		const direct = childrenOf(nodeId);
		let count = direct.length;
		for (const c of direct) count += descendantCount(c.id);
		return count;
	}

	// Roots-lane size signal — three-bar visual on each root row replaces the
	// bare descendant count. Threshold-bucketed so a quick scan tells you tree
	// scale without reading numbers.
	function sizeBucket(nodeId) {
		const dc = descendantCount(nodeId);
		if (dc < 10) return 1;
		if (dc < 50) return 2;
		return 3;
	}

	// Max updated_at across self + every descendant — the "is this tree alive"
	// signal. Root might be 6 months old but if a leaf was added yesterday the
	// tree is active. Computed fresh per call (allNodes is small enough that
	// memoization isn't worth the staleness risk on refresh).
	function subtreeMaxUpdatedAt(nodeId) {
		const node = allNodes.find(n => n.id === nodeId);
		if (!node) return null;
		let maxMs = node.updated_at ? new Date(node.updated_at).getTime() : 0;
		for (const c of childrenOf(nodeId)) {
			const childIso = subtreeMaxUpdatedAt(c.id);
			if (childIso) {
				const childMs = new Date(childIso).getTime();
				if (childMs > maxMs) maxMs = childMs;
			}
		}
		return maxMs > 0 ? new Date(maxMs).toISOString() : null;
	}

	// Days/months/years compression — keeps the column tight:
	// 0-31d → "Xd" (0d is correct for "touched in the last 24h"), 32-364d →
	// "1m"-"12m", 365+d → "Xy".
	function formatAge(dateStr) {
		if (!dateStr) return '';
		const ms = Date.now() - new Date(dateStr).getTime();
		const days = Math.floor(ms / 86400000);
		if (days < 0) return '0d';
		if (days < 32) return `${days}d`;
		if (days < 365) return `${Math.floor(days / 30)}m`;
		return `${Math.floor(days / 365)}y`;
	}

	function parentOf(node) {
		if (!node || !node.parent_id) return null;
		return allNodes.find(n => n.id === node.parent_id) || null;
	}

	function breadcrumb(node) {
		const trail = [];
		let cur = parentOf(node);
		while (cur) {
			trail.unshift(cur);
			cur = parentOf(cur);
		}
		return trail;
	}

	function shortDomain(d) {
		const parts = (d || '').split('/');
		return parts.length > 1 ? parts[parts.length - 1] : d.split('-').slice(0, 3).join(' ');
	}

	// Title-case version of the shortened domain — used when the domain is
	// promoted to a headline (H1, page title). "teekay digital warranty"
	// → "Teekay Digital Warranty".
	function titleDomain(d) {
		return shortDomain(d)
			.split(' ')
			.map(w => (w ? w[0].toUpperCase() + w.slice(1) : w))
			.join(' ');
	}

	function formatGenerality(g) {
		return '.' + ((g || 0) * 100).toFixed(0).padStart(2, '0');
	}

	// Header timestamp on a concept — "Apr 26, 2026 at 14:32".
	// Date+time precision because re-ingests within a day are common; the
	// reader needs to know if they're looking at fresh output or a cached view.
	function formatUpdatedAt(iso) {
		if (!iso) return '';
		const d = new Date(iso);
		if (isNaN(d.getTime())) return iso;
		const datePart = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
		const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
		return `${datePart} at ${timePart}`;
	}

	function tierLabel(g) {
		if (g >= 0.65) return 'trunk';
		if (g >= 0.35) return 'branch';
		return 'leaf';
	}

	function tierClass(g) {
		if (g >= 0.65) return 'ct-tier-trunk';
		if (g >= 0.35) return 'ct-tier-branch';
		return 'ct-tier-leaf';
	}

	// Provenance: was this node initiated by a human (document upload / email /
	// ingestion pipeline) or by an AI agent (MCP emission / Gardener synthesis)?
	// Used for the filter and for the badge, which both reflect node birth-origin.
	// 'meld' is legacy (pre-2026-04-20 rename); 'mcp' is the current value; both
	// mean "agent". 'gardener' is structural synthesis.
	function isAgentOrigin(node) {
		const o = (node.origin || '').toLowerCase();
		return o === 'meld' || o === 'mcp' || o === 'gardener' || o.includes('emit');
	}

	// originClass drives the subtle row tint in the explorer. Uses
	// `latest_authored_by` (last writer on the most recent contribution) so the
	// tint evolves with content, not just birth-origin. Falls back to the node's
	// origin when there's no contribution yet (skeleton-only shelves). Truly
	// unclassifiable nodes fall through to the neutral 'system' tint — more
	// honest than coloring legacy pre-contributions-table nodes as 'human'.
	function originClass(node) {
		const last = (node.latest_authored_by || '').toLowerCase();
		if (last === 'agent') return 'ct-origin-agent';
		if (last === 'human') return 'ct-origin-human';
		if (last === 'system') return 'ct-origin-system';
		const o = (node.origin || '').toLowerCase();
		if (!o) return 'ct-origin-system';
		return isAgentOrigin(node) ? 'ct-origin-agent' : 'ct-origin-human';
	}


	function escHtml(str) {
		return (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	// Render markdown via the global `marked` library loaded in index.html.
	// Body content from agents/ingestion often carries markdown (**bold**,
	// `code`, lists, etc.). Without rendering, those markers display as
	// literal text. Falls back to escaped plain text if marked is missing.
	function renderMarkdown(text) {
		if (text == null) return '';
		const s = String(text);
		// Marked v11 UMD exports the parse function as window.marked itself
		// (callable), NOT an object with .parse. Earlier feature-detection
		// for .parse silently failed. Try both API shapes.
		if (typeof window !== 'undefined' && window.marked) {
			try {
				if (typeof window.marked === 'function') return window.marked(s);
				if (typeof window.marked.parse === 'function') return window.marked.parse(s);
			} catch { /* fall through to plain */ }
		}
		return escHtml(s);
	}

	// =========================================================================
	// Computed views
	// =========================================================================


	// Recency window: oldest node timestamp defines the far end of the slider.
	// Nodes without a timestamp stay visible (can't be placed on the curve).
	$: oldestTimestamp = allNodes.length > 0
		? Math.min(...allNodes
			.map(n => new Date(n.updated_at || n.created_at || Date.now()).getTime())
			.filter(t => !isNaN(t) && t > 0))
		: Date.now();

	// Cubic curve: recency^3. At r=1 returns -Infinity (no cutoff). At r=0 returns now.
	$: recencyCutoff = recency >= 1
		? -Infinity
		: Date.now() - Math.pow(recency, 3) * (Date.now() - oldestTimestamp);

	$: recencyLabel = (() => {
		if (recency >= 1) return '∞';
		const ageMs = Math.pow(recency, 3) * (Date.now() - oldestTimestamp);
		const days = ageMs / 86400000;
		if (days < 1) return '<1d';
		if (days < 30) return `${Math.round(days)}d`;
		if (days < 365) return `${Math.round(days / 30)}mo`;
		return `${Math.round(days / 365)}y`;
	})();

	// Root lane: every parent_id=null node is a trunk, regardless of generality.
	// We group by primary domain (domains[0]) so the list reads like a database
	// schema browser — scannable clusters instead of a flat 40-row list.
	// Within each domain group, sort by generality DESC. Domain groups
	// themselves are ordered by largest-cluster-first (big subjects rise).
	//
	// Unscoped nodes (no domain) collect under "(unscoped)" at the bottom.
	$: rootGroups = (() => {
		const bareRoots = allNodes.filter(n => {
			if (n.parent_id) return false;
			if (recencyCutoff > -Infinity) {
				const t = new Date(n.updated_at || n.created_at || 0).getTime();
				if (!isNaN(t) && t > 0 && t < recencyCutoff) return false;
			}
			return true;
		});

		const q = searchQuery.trim().toLowerCase();
		const matches = q
			? bareRoots.filter(r =>
				r.concept.toLowerCase().includes(q)
				|| (r.domains || []).some(d => shortDomain(d).toLowerCase().includes(q))
			)
			: bareRoots;

		// Two-bucket rule (BY DESIGN — see Concept Tree — Living Tree vs File
		// Cabinet Schema discovery 2026-04-25): the Explorer's lane-1 axis is
		// always exactly { System | tenant }. The domains array is data
		// plumbing, not a user-facing organization principle — so we never
		// derive lane-1 schema labels from domain slugs. All tenant content
		// lands under the tenant header regardless of domain[0]; system docs
		// land under "System". Hierarchy inside the tenant group comes from
		// the concept tree's parent_id chains, not flat domain grouping.
		const systemNodes = [];
		const tenantNodes = [];
		for (const node of matches) {
			const primary = (node.domains && node.domains[0]) || null;
			if (primary === SYSTEM_DOMAIN_KEY) systemNodes.push(node);
			else tenantNodes.push(node);
		}

		const sortNodes = (nodes) => {
			nodes.sort((a, b) => {
				if (rootsSortMode === 'alphabetical') {
					const lex = (a.concept || '').localeCompare(b.concept || '');
					if (lex !== 0) return lex;
					return (b.generality || 0) - (a.generality || 0);
				}
				if (rootsSortMode === 'date') {
					// Newest tree first. subtreeMaxUpdatedAt reuses the exact
					// signal the age column displays, so the visible age value
					// matches the sort order — no cognitive translation needed.
					// Falls back to root's own updated_at when the subtree walk
					// returns null (defensive — shouldn't happen with a healthy
					// tree but keeps the comparator total).
					const aT = new Date(subtreeMaxUpdatedAt(a.id) || a.updated_at || 0).getTime();
					const bT = new Date(subtreeMaxUpdatedAt(b.id) || b.updated_at || 0).getTime();
					if (bT !== aT) return bT - aT;
					return (a.concept || '').localeCompare(b.concept || '');
				}
				// generality (default fallthrough)
				const gd = (b.generality || 0) - (a.generality || 0);
				if (gd !== 0) return gd;
				return (a.concept || '').localeCompare(b.concept || '');
			});
			return nodes;
		};

		const groups = [];
		if (systemNodes.length > 0) {
			groups.push({
				domain: SYSTEM_DOMAIN_KEY,
				label: 'System',
				isSystem: true,
				nodes: sortNodes(systemNodes),
			});
		}
		if (tenantNodes.length > 0) {
			groups.push({
				domain: '__tenant__',
				label: tenantDisplayName,
				isSystem: false,
				isOther: true,
				nodes: sortNodes(tenantNodes),
			});
		}
		return groups;
	})();

	// Flat list for backward-compatible consumers (keyboard nav, count headers).
	$: rootNodes = rootGroups.flatMap(g => g.nodes);

	// Header "+/−" button state: true when every group is currently in
	// collapsedGroups. Empty list → treated as expanded so the button shows "−".
	$: allGroupsCollapsed = rootGroups.length > 0
		&& rootGroups.every(g => collapsedGroups.has(g.domain || '__unscoped__'));

	$: selectedRoot = selectedRootId ? allNodes.find(n => n.id === selectedRootId) : null;

	$: detailNode = selectedNodeId ? allNodes.find(n => n.id === selectedNodeId) : selectedRoot;
	$: detailBreadcrumb = detailNode ? breadcrumb(detailNode) : [];
	$: detailChildren = detailNode ? childrenOf(detailNode.id).sort((a, b) => b.generality - a.generality) : [];
	$: detailSentenceEnd = detailNode?.context ? detailNode.context.search(/[.!?]\s/) : -1;

	// Leaves of a branch — every descendant at any substrate depth, flattened
	// and sorted by generality desc. Three-tier visual rendering:
	// trunk → branch → leaves, regardless of actual depth.
	//
	// The substrate keeps the real hierarchy intact (parent_id chains, depths,
	// gardener operates on it as-is). This view-layer flatten is purely for
	// reading: when Cartographer produces a ladder-shaped skeleton (e.g. a
	// single-file artifact decomposed into 4-5 redundant intermediate nodes
	// at slipping generalities), the substrate carries the redundancy but the
	// user sees a clean flat list of atomic concepts under each branch.
	// Reading order is broadest leaf first, atomic facts last — matches how a
	// reader scans a technical reference.
	function leavesOf(branchId) {
		const ids = collectDescendants(branchId);
		ids.delete(branchId); // exclude the branch itself
		return [...ids]
			.map(id => allNodes.find(n => n.id === id))
			.filter(Boolean)
			.sort((a, b) => b.generality - a.generality);
	}

	function leafValue(leaf) {
		if (!leaf || !leaf.content || typeof leaf.content !== 'object') return '';
		return Object.values(leaf.content)
			.filter(v => v && typeof v === 'string')
			.join(' ')
			.trim();
	}

	// Total count of branches + their leaves, for Lane 3 header
	$: detailNestedCount = detailChildren.reduce((sum, c) => sum + 1 + leavesOf(c.id).length, 0);

	// =========================================================================
	// Selection handlers
	// =========================================================================

	function selectRoot(id) {
		if (selectedRootId === id) return;
		selectedRootId = id;
		selectedNodeId = null;
		if (isMobile) mobileLane = 'concept';
	}

	function selectNode(id) {
		if (selectedNodeId === id) return;
		selectedNodeId = id;
	}

	function navigateToNode(id) {
		const node = allNodes.find(n => n.id === id);
		if (!node) return;
		let cur = node;
		while (cur.parent_id) {
			if (cur.parent_id === selectedRootId) { selectedNodeId = id; if (isMobile) mobileLane = 'concept'; return; }
			cur = allNodes.find(n => n.id === cur.parent_id) || { parent_id: null };
		}
		selectedRootId = cur.id;
		selectedNodeId = cur.id === id ? null : id;
		if (isMobile) mobileLane = 'concept';
	}

	function setDomain(d) {
		activeDomain = activeDomain === d ? null : d;
		selectedRootId = null;
		selectedNodeId = null;
	}

	function scrollToSection(id, container) {
		if (isMobile) {
			mobileLane = 'concept';
			// Defer scroll until lane is visible after Svelte tick
			setTimeout(() => {
				if (!conceptScrollEl) return;
				const el = conceptScrollEl.querySelector(`[data-section-id="${id}"]`);
				if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}, 50);
			return;
		}
		if (!container) return;
		const el = container.querySelector(`[data-section-id="${id}"]`);
		if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	// =========================================================================
	// Modal
	// =========================================================================

	function peekNode(node) {
		const children = childrenOf(node.id).sort((a, b) => b.generality - a.generality);
		const parent = parentOf(node);
		const crumbs = breadcrumb(node);
		let contentHtml = '';
		if (node.content && typeof node.content === 'object') {
			for (const [domain, text] of Object.entries(node.content)) {
				if (text) contentHtml += `<div class="ct-modal-domain-block"><div class="ct-modal-domain-label">${escHtml(shortDomain(domain))}</div><div class="ct-modal-domain-text">${escHtml(text)}</div></div>`;
			}
		}
		modal = { node, parent, children, crumbs, contentHtml };
		showMetadata = false;
	}

	function closeModal() { modal = null; showMetadata = false; }

	function openFull(node) {
		if (!node) return;
		window.open('/concept/' + node.id, '_blank', 'noopener');
	}

	// =========================================================================
	// Markdown export — mirrors the middle lane rendering
	// =========================================================================

	function contentBlocks(content, headingLevel) {
		if (!content || typeof content !== 'object') return [];
		const prefix = '#'.repeat(headingLevel);
		const out = [];
		for (const [domain, text] of Object.entries(content)) {
			if (!text || typeof text !== 'string') continue;
			out.push(`${prefix} ${shortDomain(domain)}`);
			out.push('');
			out.push(text.trim());
			out.push('');
		}
		return out;
	}

	// Mirrors `v4-preview-lead`: semibolds the first sentence of a context string.
	function contextWithLead(context) {
		if (!context) return '';
		const trimmed = context.trim();
		const match = trimmed.search(/[.!?]\s/);
		if (match === -1) return `**${trimmed}**`;
		const lead = trimmed.substring(0, match + 1);
		const rest = trimmed.substring(match + 1).trim();
		return rest ? `**${lead}** ${rest}` : `**${lead}**`;
	}

	// Indent a block of text under a bullet so it renders as part of the list item.
	function indentUnderBullet(text) {
		return text.split('\n').map(l => (l ? '    ' + l : '')).join('\n');
	}

	/** Strip %%{init}%% directive and classDef — global mermaid config handles styling. */
	function cleanMermaid(code) {
		if (!code) return '';
		return code
			.replace(/%%\{init:.*?}%%\n?/s, '')
			.replace(/\n\s*classDef default[^\n]*/g, '')
			// Strip any `font-size:Npx` or `font-size:Npt` fragment out of classDef
			// declarations. The pipeline diagrammer bakes 17px directly into each
			// classDef, which becomes an inline SVG style and beats CSS. Removing
			// it here lets the global Mermaid initialize() fontSize + our lane
			// CSS rules take effect.
			.replace(/,?\s*font-size\s*:\s*\d+(?:\.\d+)?\s*(px|pt|em|rem)?/gi, '')
			.trim();
	}

	function slugify(str) {
		return (str || 'concept')
			.toLowerCase()
			.replace(/[^a-z0-9\s-]/g, '')
			.replace(/\s+/g, '-')
			.replace(/-+/g, '-')
			.replace(/^-|-$/g, '')
			.slice(0, 60);
	}

	function buildMarkdown(root, children) {
		const lines = [];

		// ── Root section ────────────────────────────────────────────────
		// Prefer the collection identity (primary domain) as the H1 — it's
		// the most recognizable anchor for a reader looking at a tree
		// sourced from one collection. The concept name ("Digital Compliance
		// Document Management") becomes the conceptual lens on the meta line.
		// Falls back to the concept when no domain is present (e.g., pure
		// Meld emissions with no domain_hint).
		lines.push(`# ${root.concept}`);
		lines.push('');

		const rootMeta = [
			`\`${formatGenerality(root.generality)}\``,
			tierLabel(root.generality)
		];
		if (isAgentOrigin(root)) rootMeta.push('mind');
		if (root.domains?.length > 0) {
			rootMeta.push(root.domains.map(shortDomain).join(' · '));
		}
		lines.push(`*${rootMeta.join(' — ')}*`);
		lines.push('');

		if (root.context) {
			lines.push(contextWithLead(root.context));
			lines.push('');
		}

		// Content blocks as subheadings (mirrors lane 2's domain-over-text blocks)
		lines.push(...contentBlocks(root.content, 3));

		if (root.source_files?.length) {
			lines.push(`**Sources (${root.source_files.length})**`);
			lines.push('');
			for (const sf of root.source_files) {
				lines.push(`- ${sf.filename || sf.domain || 'unknown'}`);
			}
			lines.push('');
		}

		if (root.entities?.length) {
			lines.push(`**Entities:** ${root.entities.join(' · ')}`);
			lines.push('');
		}

		if (root.diagram?.mermaid_code) {
			lines.push('```mermaid');
			lines.push(root.diagram.mermaid_code);
			lines.push('```');
			lines.push('');
		}

		if (children.length === 0) {
			return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
		}

		// ── Children (flow continuously, no wrapper heading) ───────────
		lines.push('---');
		lines.push('');

		for (const child of children) {
			lines.push(`## ${child.concept}  \`${formatGenerality(child.generality)}\``);
			lines.push('');

			const childMeta = [tierLabel(child.generality)];
			if (isAgentOrigin(child)) childMeta.push('mind');
			if (child.domains?.length) {
				childMeta.push(child.domains.map(shortDomain).join(' · '));
			}
			if (childMeta.length > 0) {
				lines.push(`*${childMeta.join(' — ')}*`);
				lines.push('');
			}

			if (child.context) {
				lines.push(child.context.trim());
				lines.push('');
			}

			lines.push(...contentBlocks(child.content, 4));

			if (child.entities?.length) {
				lines.push(`**Entities:** ${child.entities.join(' · ')}`);
				lines.push('');
			}

			if (child.diagram?.mermaid_code) {
				lines.push('```mermaid');
				lines.push(child.diagram.mermaid_code);
				lines.push('```');
				lines.push('');
			}

			// Leaves as indented bullets so values render nested beneath the name
			const leaves = leavesOf(child.id);
			if (leaves.length > 0) {
				for (const leaf of leaves) {
					const tag = `\`${formatGenerality(leaf.generality)}\``;
					lines.push(`- **${leaf.concept}** ${tag}`);
					const val = leafValue(leaf);
					if (val) {
						lines.push(indentUnderBullet(val.trim()));
					}
				}
				lines.push('');
			}
		}

		return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
	}

	function downloadMarkdown() {
		if (!selectedRoot) return;
		const md = buildMarkdown(selectedRoot, detailChildren);
		const today = new Date().toISOString().slice(0, 10);
		const filename = `${slugify(selectedRoot.concept)}-${today}.md`;
		const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	let emailing = false;
	let emailed = false;

	async function emailMe() {
		if (!selectedRoot || emailing) return;
		emailing = true;
		emailed = false;
		try {
			const md = buildMarkdown(selectedRoot, detailChildren);
			await emailConceptSnapshot({
				title: selectedRoot.concept,
				markdown: md,
				generality: selectedRoot.generality,
				tier: tierLabel(selectedRoot.generality)
			});
			emailed = true;
			setTimeout(() => { emailed = false; }, 4000);
		} catch {
			/* silent */
		}
		emailing = false;
	}

	// Reset the Sent state whenever the selected root changes.
	$: if (selectedRootId) { emailed = false; }

	// ── Prune mode ──

	function collectDescendants(nodeId) {
		const ids = new Set();
		ids.add(nodeId);
		const queue = [nodeId];
		while (queue.length) {
			const pid = queue.shift();
			for (const n of allNodes) {
				if (n.parent_id === pid && !ids.has(n.id)) {
					ids.add(n.id);
					queue.push(n.id);
				}
			}
		}
		return ids;
	}

	function togglePruneNode(nodeId) {
		if (!pruneMode) return;
		const next = new Set(pruneSelected);
		const descendants = collectDescendants(nodeId);
		if (next.has(nodeId)) {
			for (const id of descendants) next.delete(id);
		} else {
			for (const id of descendants) next.add(id);
		}
		pruneSelected = next;
	}

	function startPruneHold() {
		if (pruneSelected.size === 0 || pruning) return;
		pruneHolding = true;
		pruneHoldProgress = 0;
		pruneHoldInterval = setInterval(() => {
			pruneHoldProgress += 2;
			if (pruneHoldProgress >= 100) {
				clearInterval(pruneHoldInterval);
				pruneHoldInterval = null;
				executePrune();
			}
		}, 30);
	}

	function cancelPruneHold() {
		pruneHolding = false;
		pruneHoldProgress = 0;
		if (pruneHoldInterval) {
			clearInterval(pruneHoldInterval);
			pruneHoldInterval = null;
		}
	}

	async function executePrune() {
		pruneHolding = false;
		pruneHoldProgress = 0;
		pruning = true;
		try {
			// Send only top-level roots — backend cascades to descendants via recursive CTE.
			// A node is a "root" of the selection if its parent is not also selected.
			const roots = Array.from(pruneSelected).filter(id => {
				const node = allNodes.find(n => n.id === id);
				return !node || !node.parent_id || !pruneSelected.has(node.parent_id);
			});
			await trashConceptNodes(roots);
			allNodes = allNodes.filter(n => !pruneSelected.has(n.id));
			if (pruneSelected.has(selectedRootId)) {
				selectedRootId = null;
				selectedNodeId = null;
			}
			pruneSelected = new Set();
			pruneMode = false;
		} catch (err) {
			console.error('Prune failed:', err);
		}
		pruning = false;
	}

	function exitPrune() {
		pruneMode = false;
		pruneSelected = new Set();
		cancelPruneHold();
	}

	// Touch equivalents for prune hold-to-remove
	function handlePruneTouchStart(e) {
		e.preventDefault();
		startPruneHold();
	}
	function handlePruneTouchEnd(e) {
		e.preventDefault();
		cancelPruneHold();
	}

	function handleKeydown(e) {
		if (e.key === 'Escape' && pruneMode) { exitPrune(); return; }
		if (e.key === 'Escape' && modal) closeModal();
	}

	function handleWindowClick() {
		if (showOverflow) showOverflow = false;
	}

	onMount(() => {
		checkMobile();
		window.addEventListener('resize', checkMobile);
		loadTree();
		return () => window.removeEventListener('resize', checkMobile);
	});

	afterUpdate(() => {
		if (!window.mermaid) return;
		// Reset data-processed on any .mermaid element that does NOT already
		// contain a rendered svg. Mermaid marks processed elements so it
		// won't re-render, but when Svelte reactively swaps the text inside
		// a <pre class="mermaid"> (e.g. navigating to a different concept),
		// the element has new text but still carries data-processed="true"
		// from the prior diagram. Mermaid then skips it, leaving the new
		// text as raw code. This reset makes mermaid.run() re-process
		// everything that doesn't currently show a diagram.
		const pending = [];
		document.querySelectorAll('.ct-diagram .mermaid').forEach(el => {
			if (!el.querySelector('svg')) {
				el.removeAttribute('data-processed');
				pending.push(el);
			}
		});
		// Only hide diagrams that AFFIRMATIVELY rendered with error markers.
		// No svg = still rendering or unrendered = leave alone; svg with
		// error class = hide.
		const hideFailed = () => {
			document.querySelectorAll('.ct-diagram').forEach(el => {
				const svg = el.querySelector('svg');
				if (!svg) return;
				const hasErrorClass =
					svg.getAttribute('aria-roledescription') === 'error' ||
					svg.classList.contains('flowchart-error') ||
					svg.classList.contains('error') ||
					el.querySelector('.error-icon');
				if (hasErrorClass) {
					el.style.display = 'none';
					el.setAttribute('data-mermaid-failed', 'true');
				}
			});
		};
		// Pass explicit nodes so mermaid can't miss them. In v11 the default
		// querySelector was unreliable once we moved from <pre> to <div>.
		const run = pending.length > 0
			? window.mermaid.run({ nodes: pending, suppressErrors: true })
			: window.mermaid.run({ suppressErrors: true });
		(run && typeof run.then === 'function') ? run.then(hideFailed).catch(hideFailed) : setTimeout(hideFailed, 150);
	});
</script>

<svelte:window on:keydown={handleKeydown} on:click={handleWindowClick} />
<svelte:head><title>Concepts - ADRASI</title></svelte:head>

<div class="v4-page">
<Navbar active="explore" {navigate} on:refresh={refreshAll} />

<!-- Sub-navbar: search (left) + recency (center). Type pills + generality
     slider removed — root lane shows trunks by fixed threshold, no filter
     switching. -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<div class="v4-subnav">
	<div class="v4-search-wrap">
		<input class="v4-search-input" type="text" bind:value={searchQuery} placeholder="Search" autocomplete="off" spellcheck="false" />
		{#if searchQuery}
			<button class="v4-search-clear" aria-label="Clear search" on:click={() => searchQuery = ''}>x</button>
		{:else}
			<span class="v4-search-icon"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="5" cy="5" r="3.5"/><line x1="7.8" y1="7.8" x2="11" y2="11"/></svg></span>
		{/if}
	</div>

	<label class="v4-filter v4-filter-center">
		<span class="v4-filter-label">Recency</span>
		<input class="v4-filter-slider" type="range" min="0" max="1" step="0.01" bind:value={recency} />
		<span class="v4-filter-value">{recencyLabel}</span>
	</label>

	<div class="v4-subnav-spacer"></div>
</div>

<!-- Mobile-only: collapsed subnav with search + filter toggle -->
<div class="v4-subnav-mobile">
	<div class="v4-search-wrap v4-search-wrap-mobile">
		<input class="v4-search-input" type="text" bind:value={searchQuery} placeholder="Search concepts" autocomplete="off" spellcheck="false" />
		{#if searchQuery}
			<button class="v4-search-clear" aria-label="Clear search" on:click={() => searchQuery = ''}>x</button>
		{:else}
			<span class="v4-search-icon"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="5" cy="5" r="3.5"/><line x1="7.8" y1="7.8" x2="11" y2="11"/></svg></span>
		{/if}
	</div>
	<button class="v4-filter-toggle" class:v4-filter-toggle-active={mobileFiltersOpen} on:click={() => mobileFiltersOpen = !mobileFiltersOpen}>Filters</button>
</div>
{#if mobileFiltersOpen}
	<div class="v4-filter-drawer">
		<label class="v4-filter">
			<span class="v4-filter-label">Recency</span>
			<input class="v4-filter-slider" type="range" min="0" max="1" step="0.01" bind:value={recency} />
			<span class="v4-filter-value">{recencyLabel}</span>
		</label>
	</div>
{/if}

<!-- Mobile-only: lane tab strip -->
<div class="v4-mobile-tabs">
	<button class="v4-mobile-tab" class:v4-mobile-tab-active={mobileLane === 'roots'} on:click={() => mobileLane = 'roots'}>
		Roots{#if rootNodes.length > 0}<span class="v4-mobile-tab-count">{rootNodes.length}</span>{/if}
	</button>
	<button class="v4-mobile-tab" class:v4-mobile-tab-active={mobileLane === 'concept'} on:click={() => mobileLane = 'concept'}>
		Concept
	</button>
	<button class="v4-mobile-tab" class:v4-mobile-tab-active={mobileLane === 'outline'} on:click={() => mobileLane = 'outline'}>
		Outline{#if detailNestedCount > 0}<span class="v4-mobile-tab-count">{detailNestedCount}</span>{/if}
	</button>
</div>

{#if loading}
	<div class="v4-loading">Loading...</div>
{:else}
	<div class="v4-columns" class:v4-columns-outline-collapsed={outlineLaneCollapsed}>
		<!-- LANE 1: Roots — grouped by primary domain (schema-browser pattern) -->
		<div class="v4-lane" class:v4-mobile-lane-hidden={mobileLane !== 'roots'}>
			<div class="v4-lane-header">
				Roots<span class="v4-lane-header-sub"> — {rootNodes.length}</span>
				{#if rootGroups.length > 0}
					<button
						class="v4-lane-toggle-all v4-lane-sort-toggle"
						title={sortMeta.title}
						aria-label={sortMeta.aria}
						on:click={toggleRootsSort}
					>{sortMeta.label}</button>
					<button
						class="v4-lane-toggle-all"
						title={allGroupsCollapsed ? 'Expand all schemas' : 'Collapse all schemas'}
						aria-label={allGroupsCollapsed ? 'Expand all schemas' : 'Collapse all schemas'}
						on:click={toggleAllGroups}
					>{allGroupsCollapsed ? '+' : '−'}</button>
				{/if}
			</div>
			<div class="v4-lane-scroll">
				{#each rootGroups as group (group.domain || '__unscoped__')}
					{@const groupKey = group.domain || '__unscoped__'}
					{@const isCollapsed = collapsedGroups.has(groupKey)}
					<div class="v4-group" class:v4-group-collapsed={isCollapsed} class:v4-group-system={group.isSystem}>
						<button type="button" class="v4-group-header" on:click={() => toggleGroup(groupKey)} aria-expanded={!isCollapsed}>
							<span class="v4-group-chevron" aria-hidden="true">{isCollapsed ? '▸' : '▾'}</span>
							<span class="v4-group-label">{group.label}</span>
							<span class="v4-group-count">{group.nodes.length}</span>
						</button>
						{#if !isCollapsed}
							{#each group.nodes as node (node.id)}
								{@const dc = descendantCount(node.id)}
								{@const bucket = sizeBucket(node.id)}
								{@const age = formatAge(subtreeMaxUpdatedAt(node.id))}
								<div class="v4-item" class:v4-item-selected={selectedRootId === node.id} class:ct-prune-marked={pruneMode && pruneSelected.has(node.id)} on:click={() => pruneMode ? togglePruneNode(node.id) : selectRoot(node.id)}>
									<span class="v4-item-label">{node.concept}</span>
									{#if !group.isSystem}
										<span class="ct-size-bars" title="{dc} descendant{dc === 1 ? '' : 's'}" aria-label="{dc} descendants">
											<span class="ct-size-bar" class:ct-size-bar-on={bucket >= 1}></span>
											<span class="ct-size-bar" class:ct-size-bar-on={bucket >= 2}></span>
											<span class="ct-size-bar" class:ct-size-bar-on={bucket >= 3}></span>
										</span>
										<span class="v4-item-age" title="last updated">{age}</span>
									{/if}
								</div>
							{/each}
						{/if}
					</div>
				{/each}
				{#if rootNodes.length === 0}
					<div class="v4-lane-empty">No root concepts</div>
				{/if}
			</div>
		</div>

		<!-- LANE 2: Continuous detail (middle, wide) -->
		<div class="v4-lane ct-lane-content" class:ct-prune-active={pruneMode} class:v4-mobile-lane-hidden={mobileLane !== 'concept'}>
			<div class="v4-lane-header">
				{#if pruneMode}
					<span class="ct-prune-indicator">Pruning</span>
					<span class="ct-prune-count">{pruneSelected.size} selected</span>
					<button class="ct-lane-action ct-prune-cancel" on:click|stopPropagation={exitPrune}>Cancel</button>
					<button
						class="ct-lane-action ct-prune-confirm"
						class:ct-prune-holding={pruneHolding}
						disabled={pruneSelected.size === 0 || pruning}
						on:mousedown={startPruneHold}
						on:mouseup={cancelPruneHold}
						on:mouseleave={cancelPruneHold}
						on:touchstart|preventDefault={handlePruneTouchStart}
						on:touchend|preventDefault={handlePruneTouchEnd}
						on:touchcancel={cancelPruneHold}
						style="--hold-progress: {pruneHoldProgress}%"
					>{pruning ? 'Removing…' : 'Hold to remove'}</button>
				{:else}
					{selectedRoot ? (selectedRoot.concept) : 'Detail'}
					{#if selectedRoot}
						<div class="ct-overflow-wrap">
							<button class="ct-overflow-btn" on:click|stopPropagation={() => showOverflow = !showOverflow}><CircleEllipsis size={22} strokeWidth={1.5} /></button>
							{#if showOverflow}
								<div class="ct-overflow-menu" on:click|stopPropagation={() => showOverflow = false}>
									<button on:click={downloadMarkdown}>Download MD</button>
									<button on:click={emailMe} disabled={emailing || emailed}>
										{emailed ? 'Sent' : emailing ? 'Sending…' : 'Email me'}
									</button>
									<button on:click={() => openFull(selectedRoot)}>Open Full</button>
									<div class="ct-overflow-divider"></div>
									<button on:click={openCorrectModal}>Alter</button>
									<button on:click={() => { pruneMode = true; showOverflow = false; }}>Prune</button>
								</div>
							{/if}
						</div>
					{/if}
				{/if}
			</div>
			<div class="v4-lane-scroll" bind:this={conceptScrollEl}>
				{#if !selectedRoot}
					<div class="v4-lane-empty">Select a concept to preview</div>
				{:else}
					<!-- Root detail -->
					<div class="ct-section" class:ct-prune-marked={pruneMode && pruneSelected.has(selectedRoot.id)} data-section-id={selectedRoot.id} on:click={() => pruneMode && togglePruneNode(selectedRoot.id)}>
						<div class="ct-section-heading">{selectedRoot.concept}</div>
						<div class="ct-detail-meta">
							<span class="ct-generality-large {tierClass(selectedRoot.generality)}">{formatGenerality(selectedRoot.generality)}</span>
							<span class="ct-tier-label">{tierLabel(selectedRoot.generality)}</span>
							{#if isAgentOrigin(selectedRoot)}<span class="ct-origin-badge">mind</span>{/if}
							{#if (selectedRoot.domains || []).length > 0}
								<span class="ct-domain-tags">{#each (selectedRoot.domains || []) as d}<span class="ct-domain-tag">{shortDomain(d)}</span>{/each}</span>
							{/if}
						</div>
						{#if selectedRoot.updated_at}
							<div class="ct-updated-at">Last updated {formatUpdatedAt(selectedRoot.updated_at)}</div>
						{/if}
						{#if selectedRoot.context}
							<p class="v4-preview-text">
								{#if detailSentenceEnd !== -1}
									<span class="v4-preview-lead">{selectedRoot.context.substring(0, detailSentenceEnd + 1)}</span>{selectedRoot.context.substring(detailSentenceEnd + 1)}
								{:else}
									<span class="v4-preview-lead">{selectedRoot.context}</span>
								{/if}
							</p>
						{/if}
						<!-- At-a-glance orientation metadata stamped by the Orienter pipeline stage. -->
						<OrientationBlock orientation={selectedRoot.content?.orientation} />
						{#if selectedRoot.content && typeof selectedRoot.content === 'object'}
							{#if selectedRoot._system && descendantCount(selectedRoot.id) > 0}
								<!-- System root renders its sections as children below — skip the full-body block to avoid duplicating the rubric text. -->
							{:else if selectedRoot._system && selectedRoot.content.markdown && Object.keys(selectedRoot.content).length === 1}
								<div class="ct-content-block ct-content-system"><div class="ct-content-text">{@html renderMarkdown(selectedRoot.content.markdown)}</div></div>
							{:else}
								{#each Object.entries(selectedRoot.content).filter(([k, v]) => k !== 'orientation' && typeof v === 'string') as [domain, text]}
									{#if text}
										<div class="ct-content-block"><div class="ct-content-domain">{shortDomain(domain)}</div><div class="ct-content-text">{@html renderMarkdown(text)}</div></div>
									{/if}
								{/each}
							{/if}
						{/if}
						{#if selectedRoot.source_files?.length}
							<div class="v4-preview-sections">
								<div class="v4-preview-sections-label">Sources ({selectedRoot.source_files.length})</div>
								{#each selectedRoot.source_files as sf}<div class="ct-source-item">{sf.filename || sf.domain || 'unknown'}</div>{/each}
							</div>
						{/if}
						{#if selectedRoot.entities?.length}
							<div class="ct-entities">{#each selectedRoot.entities as entity}<span class="ct-entity-tag">{entity}</span>{/each}</div>
						{/if}
						{#if selectedRoot.diagram?.mermaid_code}
							<div class="ct-diagram"><div class="mermaid">{cleanMermaid(selectedRoot.diagram.mermaid_code)}</div></div>
						{/if}
					</div>
					<!-- Children TOC (click to scroll) -->
					{#if detailChildren.length > 0}
						<div class="v4-preview-sections">
							<div class="v4-preview-sections-label">Children ({detailChildren.length})</div>
							{#each detailChildren as child}
								<div
									class="ct-child-item"
									class:ct-agent-last={child.latest_authored_by === 'agent'}
									class:ct-prune-marked={pruneMode && pruneSelected.has(child.id)}
									on:click={() => pruneMode ? togglePruneNode(child.id) : scrollToSection(child.id, conceptScrollEl)}
								>
									<span class="ct-generality {tierClass(child.generality)}">{formatGenerality(child.generality)}</span>
									<span class="ct-child-concept">{child.concept}</span>
								</div>
							{/each}
						</div>
					{/if}
					<!-- Children rendered continuously, leaves inline under each branch -->
					{#each detailChildren as child (child.id)}
						<div class="ct-section ct-section-child" class:ct-prune-marked={pruneMode && pruneSelected.has(child.id)} data-section-id={child.id} on:click={() => pruneMode && togglePruneNode(child.id)}>
							<div class="ct-section-heading" class:ct-agent-last={child.latest_authored_by === 'agent'}>
								<span class="ct-generality {tierClass(child.generality)}">{formatGenerality(child.generality)}</span>
								{child.concept}
							</div>
							{#if child.context}
								<p class="v4-preview-text">{child.context}</p>
							{/if}
							<!-- Per-child OrientationBlock removed 2026-04-26 — the parent's collated
								block at the top of the lane already unions every descendant's metadata
								via the orchestrator's root-level aggregation pass. Per-child mini-blocks
								were noise (one slot each, scattered) instead of the up-front summary
								the doctrine calls for. The block re-appears when a child is selected
								as the active root via the line ~1098 selectedRoot rendering. -->
							<!-- <OrientationBlock orientation={child.content?.orientation} variant="compact" /> -->
							{#if child.content && typeof child.content === 'object'}
								{#if child._system && child.content.markdown && Object.keys(child.content).length === 1}
									<div class="ct-content-block ct-content-system"><div class="ct-content-text">{@html renderMarkdown(child.content.markdown)}</div></div>
								{:else}
									{#each Object.entries(child.content).filter(([k, v]) => k !== 'orientation' && typeof v === 'string') as [domain, text]}
										{#if text}
											<div class="ct-content-block"><div class="ct-content-domain">{shortDomain(domain)}</div><div class="ct-content-text">{@html renderMarkdown(text)}</div></div>
										{/if}
									{/each}
								{/if}
							{/if}
							{#if child.entities?.length}
								<div class="ct-entities">{#each child.entities as entity}<span class="ct-entity-tag">{entity}</span>{/each}</div>
							{/if}
							{#if child.diagram?.mermaid_code}
								<div class="ct-diagram"><div class="mermaid">{cleanMermaid(child.diagram.mermaid_code)}</div></div>
							{/if}
							{#each leavesOf(child.id) as leaf (leaf.id)}
								<div class="ct-leaf-row" class:ct-prune-marked={pruneMode && pruneSelected.has(leaf.id)} data-section-id={leaf.id} on:click|stopPropagation={() => pruneMode && togglePruneNode(leaf.id)}>
									<div class="ct-leaf-head" class:ct-agent-last={leaf.latest_authored_by === 'agent'}>
										<span class="ct-generality {tierClass(leaf.generality)}">{formatGenerality(leaf.generality)}</span>
										<span class="ct-leaf-concept">{leaf.concept}</span>
									</div>
									{#if leaf._system && leaf.content?.markdown}
										<!-- System leaves carry rich prose in content.markdown. Render
										     via marked so paragraphs / bold / lists display properly.
										     The heading is already shown via leaf.concept above, so
										     content.markdown should NOT include a leading ## heading. -->
										<div class="ct-leaf-value ct-content-system">{@html renderMarkdown(leaf.content.markdown)}</div>
									{:else if leafValue(leaf)}
										<div class="ct-leaf-value">{leafValue(leaf)}</div>
									{/if}
								</div>
							{/each}
						</div>
					{/each}
				{/if}
			</div>
		</div>

		<!-- LANE 3: Outline / quick-scroll (right, narrow). Collapsible to a
			vertical rail. Default collapsed; persists user choice in localStorage.
			Mobile keeps its own visibility logic via the tab strip. -->
		<div class="v4-lane ct-lane-outline" class:ct-lane-outline-collapsed={outlineLaneCollapsed} class:v4-mobile-lane-hidden={mobileLane !== 'outline'}>
			{#if outlineLaneCollapsed}
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<button class="ct-lane-rail" on:click={toggleOutlineLane} title="Show outline" aria-label="Show outline panel">
					<span class="ct-lane-rail-chevron">‹</span>
					<span class="ct-lane-rail-label">Outline{#if selectedRoot && detailNestedCount > 0}&nbsp;·&nbsp;{detailNestedCount}{/if}</span>
				</button>
			{:else}
				<div class="v4-lane-header">
					Outline{#if selectedRoot}<span class="v4-lane-header-sub"> — {detailNestedCount}</span>{/if}
					{#if selectedRoot && detailChildren.length > 0}
						<button
							class="v4-lane-toggle-all"
							title={outlineLeavesCollapsed ? 'Show leaves' : 'Hide leaves — branches only'}
							aria-label={outlineLeavesCollapsed ? 'Show leaves in outline' : 'Hide leaves — show branches only'}
							on:click={toggleOutlineLeaves}
						>{outlineLeavesCollapsed ? '+' : '−'}</button>
					{/if}
					<button
						class="v4-lane-toggle-all ct-lane-collapse-btn"
						title="Hide outline"
						aria-label="Hide outline panel"
						on:click={toggleOutlineLane}
					>›</button>
				</div>
				<div class="v4-lane-scroll">
					{#if !selectedRootId}
						<div class="v4-lane-empty">Select a root</div>
					{:else if detailChildren.length === 0}
						<div class="v4-lane-empty">No children</div>
					{:else}
						{#each detailChildren as child (child.id)}
							<div class="ct-outline-item" on:click={() => scrollToSection(child.id, conceptScrollEl)}>
								<span class="ct-generality {tierClass(child.generality)}">{formatGenerality(child.generality)}</span>
								<span class="ct-outline-label">{child.concept}</span>
							</div>
							{#if !outlineLeavesCollapsed}
								{#each leavesOf(child.id) as leaf (leaf.id)}
									<div class="ct-outline-item ct-outline-leaf" on:click={() => scrollToSection(leaf.id, conceptScrollEl)}>
										<span class="ct-generality {tierClass(leaf.generality)}">{formatGenerality(leaf.generality)}</span>
										<span class="ct-outline-label">{leaf.concept}</span>
									</div>
								{/each}
							{/if}
						{/each}
					{/if}
				</div>
			{/if}
		</div>
	</div>
{/if}

</div>

<!-- ════════════════════════════════════════════════════════════════════════
     CORRECT-OR-UPDATE MODAL — statement-based correction primitive.
     Lane-2 dropdown opens this. Self-contained component; component file
     handles its own overlay, classification debounce, and submission flow.
     ════════════════════════════════════════════════════════════════════════ -->
{#if correctModalOpen && correctModalConcept}
	<CorrectModal
		concept={correctModalConcept}
		allNodes={allNodes}
		onClose={closeCorrectModal}
		onSuccess={onCorrectSuccess}
	/>
{/if}

<!-- ════════════════════════════════════════════════════════════════════════
     MODAL
     ════════════════════════════════════════════════════════════════════════ -->
{#if modal}
	<div class="v4-overlay" on:click|self={closeModal}>
		<div class="v4-modal">
			<div class="v4-modal-head">
				<div class="v4-modal-head-left">
					<h2 class="v4-modal-title">{modal.node.concept}</h2>
					<span class="v4-modal-level">{tierLabel(modal.node.generality)} — {formatGenerality(modal.node.generality)}</span>
				</div>
				<div class="v4-modal-head-right">
					<button class="v4-modal-btn" class:v4-modal-btn-active={showMetadata} title="Metadata" on:click={() => showMetadata = !showMetadata}>
						<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
					</button>
					<button class="v4-modal-close" on:click={closeModal}><X size={16} strokeWidth={1.5} /></button>
				</div>
			</div>
			{#if showMetadata}
				<div class="v4-meta-panel">
					<div class="v4-meta-row"><span class="v4-meta-label">ID</span><span class="v4-meta-value">{modal.node.id}</span></div>
					{#if modal.node.slug}<div class="v4-meta-row"><span class="v4-meta-label">Slug</span><span class="v4-meta-value">{modal.node.slug}</span></div>{/if}
					<div class="v4-meta-row"><span class="v4-meta-label">Origin</span><span class="v4-meta-value">{modal.node.origin || 'pipeline-v2'}</span></div>
					<div class="v4-meta-row"><span class="v4-meta-label">Type</span><span class="v4-meta-value">{modal.node.node_type || 'concept'}</span></div>
					<div class="v4-meta-row"><span class="v4-meta-label">Generality</span><span class="v4-meta-value">{formatGenerality(modal.node.generality)}</span></div>
					<div class="v4-meta-row"><span class="v4-meta-label">Depth</span><span class="v4-meta-value">{modal.node.depth}</span></div>
					<div class="v4-meta-row"><span class="v4-meta-label">Domains</span><span class="v4-meta-value">{(modal.node.domains || []).join(', ')}</span></div>
					{#if modal.node.updated_at}<div class="v4-meta-row"><span class="v4-meta-label">Updated</span><span class="v4-meta-value">{new Date(modal.node.updated_at).toLocaleDateString()}</span></div>{/if}
				</div>
			{/if}
			<div class="v4-modal-body">
				{#if modal.crumbs.length > 0}
					<div class="ct-modal-breadcrumb">{#each modal.crumbs as crumb}<span class="ct-modal-crumb">{crumb.concept}</span><span class="ct-modal-crumb-sep"> / </span>{/each}<span class="ct-modal-crumb-current">{modal.node.concept}</span></div>
				{/if}
				{#if modal.node.context}<p class="ct-modal-context">{modal.node.context}</p>{/if}
				{@html modal.contentHtml}
				{#if modal.node.diagram?.mermaid_code}
					<div class="ct-diagram"><div class="mermaid">{cleanMermaid(modal.node.diagram.mermaid_code)}</div></div>
				{/if}
				{#if modal.children.length > 0}
					<div class="ct-modal-children"><div class="ct-modal-children-label">Children ({modal.children.length})</div>
						{#each modal.children as child}<div class="ct-modal-child" on:click={() => { closeModal(); navigateToNode(child.id); }}><span class="ct-generality {tierClass(child.generality)}">{formatGenerality(child.generality)}</span><span>{child.concept}</span></div>{/each}
					</div>
				{/if}
			</div>
		</div>
	</div>
{/if}

<style>
	/* ── Page layout ── */
	.v4-page { height: 100vh; display: flex; flex-direction: column; overflow: hidden; background: #E8EAEE; color: #1A1A1A; font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif; }
	.v4-loading { flex: 1; display: flex; align-items: center; justify-content: center; font-size: 13px; color: #4B5563; }

	/* ── Sub-navbar ── */
	/* Three-column grid so search anchors left, recency centers, right stays
	   empty for visual balance. Simpler than flex auto-margin tricks and more
	   resilient when widths change. */
	.v4-subnav { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; height: 40px; padding: 0 16px; background: #FFFFFF; border-bottom: 1px solid #D8DBE2; flex-shrink: 0; }
	.v4-subnav > .v4-search-wrap { justify-self: start; }
	.v4-subnav > .v4-filter-center { justify-self: center; }
	.v4-subnav-spacer { width: 1px; }

	/* ── Type pills ── */
	.v4-type-pills { display: flex; gap: 2px; background: #F1F3F7; border-radius: 5px; padding: 2px; }
	.v4-type-pill { font-size: 10px; font-weight: 600; color: #6B7280; background: transparent; border: none; border-radius: 4px; padding: 3px 10px; cursor: pointer; letter-spacing: 0.02em; transition: all 120ms ease-out; user-select: none; }
	.v4-type-pill:hover { color: #374151; }
	.v4-type-pill-active { color: #1A1A1A; background: #FFFFFF; box-shadow: 0 1px 2px rgba(0,0,0,0.06); }

	/* ── Filter sliders ── */
	.v4-filter-group { display: flex; align-items: center; gap: 40px; }
	.v4-filter { display: flex; align-items: center; gap: 8px; cursor: pointer; }
	.v4-filter-label { font-size: 10px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: 0.06em; user-select: none; }
	.v4-filter-slider { -webkit-appearance: none; appearance: none; width: 160px; height: 2px; background: #D8DBE2; border-radius: 1px; outline: none; cursor: pointer; }
	.v4-filter-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 12px; height: 12px; background: #6B7280; border-radius: 50%; cursor: pointer; transition: background 120ms ease-out; }
	.v4-filter-slider::-webkit-slider-thumb:hover { background: #374151; }
	.v4-filter-slider::-moz-range-thumb { width: 12px; height: 12px; background: #6B7280; border-radius: 50%; border: none; cursor: pointer; transition: background 120ms ease-out; }
	.v4-filter-slider::-moz-range-thumb:hover { background: #374151; }
	.v4-filter-value { font-size: 10px; font-weight: 600; font-variant-numeric: tabular-nums; color: #374151; min-width: 24px; text-align: right; }

	/* ── Search ── */
	.v4-search-wrap { position: relative; display: flex; align-items: center; }
	.v4-search-input { width: 180px; height: 28px; border: 1px solid #D1D5DB; border-radius: 6px; padding: 0 28px 0 10px; font-size: 12px; font-family: inherit; color: #374151; background: #FFFFFF; outline: none; transition: border-color 120ms; }
	.v4-search-input::placeholder { color: #B0B6C2; }
	.v4-search-input:focus { border-color: #9CA3AF; }
	.v4-search-clear { position: absolute; right: 8px; font-size: 14px; color: #9CA3AF; cursor: pointer; }
	.v4-search-icon { position: absolute; right: 8px; display: flex; color: #B0B6C2; pointer-events: none; }

	/* ── Columns layout ── */
	.v4-columns { display: grid; grid-template-columns: 1fr 2fr 200px; gap: 0; background: #F1F3F7; flex: 1; min-height: 0; overflow: hidden; border-top: 1px solid #D8DBE2; transition: grid-template-columns 180ms ease-out; }
	/* When Lane 3 is collapsed to its rail, shrink the grid track itself so
	   Lane 2 reclaims the freed space rather than leaving an empty 200px gutter. */
	.v4-columns-outline-collapsed { grid-template-columns: 1fr 2fr 28px; }
	.v4-lane { display: flex; flex-direction: column; min-height: 0; overflow: hidden; border-right: 1px dashed #C4C9D4; background: #F5F6FA; }
	.v4-lane:last-child { border-right: none; }
	.v4-columns > .v4-lane:first-child { background: #ECEEF3; }
	.v4-lane-header { height: 40px; box-sizing: border-box; display: flex; align-items: center; padding: 0 16px; font-size: 10px; font-weight: 600; color: #4B5563; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid #D8DBE2; flex-shrink: 0; white-space: nowrap; text-overflow: ellipsis; gap: 8px; position: relative; z-index: 10; }
	.ct-lane-action { margin-left: auto; font-size: 10px; font-weight: 500; color: #9CA3AF; background: none; border: 1px solid #D8DBE2; border-radius: 4px; padding: 2px 8px; cursor: pointer; text-transform: none; letter-spacing: 0; transition: all 120ms ease-out; }
	.ct-lane-action:hover { color: #374151; border-color: #9CA3AF; }

	/* ── Overflow menu ── */
	.ct-overflow-wrap { position: relative; margin-left: auto; }
	.ct-overflow-btn { background: none; border: none; padding: 0; color: #9CA3AF; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: color 120ms ease-out; }
	.ct-overflow-btn:hover { color: #374151; }
	.ct-overflow-menu { position: absolute; top: 100%; right: 0; margin-top: 4px; background: #FFFFFF; border: 1px solid #D8DBE2; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); z-index: 20; min-width: 140px; padding: 4px 0; }
	.ct-overflow-menu button { display: block; width: 100%; text-align: left; padding: 7px 14px; font-size: 12px; color: #374151; background: none; border: none; cursor: pointer; transition: background 80ms; }
	.ct-overflow-menu button:hover { background: #F3F4F6; }
	.ct-overflow-menu button:disabled { color: #C4C9D4; cursor: default; }
	.ct-overflow-menu button:disabled:hover { background: none; }
	.ct-overflow-divider { height: 1px; background: #E5E7EB; margin: 4px 0; }

	/* ── Prune mode ── */
	.ct-prune-active { border-top: 2px solid #DC2626; }
	.ct-prune-indicator { font-size: 10px; font-weight: 700; color: #DC2626; text-transform: uppercase; letter-spacing: 0.06em; }
	.ct-prune-count { font-size: 10px; font-weight: 400; color: #6B7280; text-transform: none; letter-spacing: 0; }
	.ct-prune-cancel { margin-left: auto; }
	.ct-prune-confirm { margin-left: 4px; color: #DC2626; border-color: #DC2626; position: relative; overflow: hidden; }
	.ct-prune-confirm:hover { color: #FFFFFF; background: #DC2626; }
	.ct-prune-confirm:disabled { color: #E5A0A0; border-color: #E5A0A0; background: none; cursor: default; }
	.ct-prune-confirm:disabled:hover { background: none; }
	.ct-prune-holding, .ct-prune-holding:hover { background: linear-gradient(to right, #DC2626 var(--hold-progress, 0%), transparent var(--hold-progress, 0%)); color: #FFFFFF; }
	.ct-prune-marked { background: #FEF2F2 !important; border-left: 3px solid #DC2626; }
	.ct-section-child.ct-prune-marked, .ct-leaf-row.ct-prune-marked { margin-top: 1px; }
	.ct-prune-marked .ct-section-heading,
	.ct-prune-marked .ct-leaf-concept,
	.ct-prune-marked .v4-item-label { color: #DC2626 !important; }
	.v4-lane-header-sub { font-weight: 400; color: #9CA3AF; text-transform: none; letter-spacing: 0; }
	/* Expand/collapse-all affordance in the Roots lane header. Visual weight
	   sits close to the header sub-count — one grade darker so it reads as
	   action, not count. Keeps the 40px header height. */
	.v4-lane-toggle-all {
		margin-left: auto;
		width: 22px;
		height: 22px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		background: transparent;
		border: 1px solid #C4C9D4;
		border-radius: 4px;
		color: #4B5563;
		font-size: 14px;
		font-weight: 500;
		line-height: 1;
		cursor: pointer;
		font-family: inherit;
		padding: 0;
		transition: background 80ms ease-out, border-color 80ms ease-out, color 80ms ease-out;
	}
	.v4-lane-toggle-all:hover {
		background: #ECEEF3;
		border-color: #9CA3AF;
		color: #111827;
	}
	/* Keyboard-only focus ring — invisible to mouse users (no `:focus`),
	   visible when the user navigates by Tab. Restores affordance the global
	   button reset stripped. Same idiom can be lifted into the shared reset
	   block when more buttons need it. */
	.v4-lane-toggle-all:focus-visible {
		outline: 2px solid #1e3a8a;
		outline-offset: 1px;
	}
	/* Sort toggle sits LEFT of the +/− (margin-left:auto on the +/− pushes it
	   to the right edge; the sort button needs its own auto-left + small gap
	   so both buttons share the right cluster with a tight 6px gap). */
	.v4-lane-sort-toggle {
		width: auto;
		min-width: 30px;
		padding: 0 6px;
		margin-left: auto;
		margin-right: 6px;
		font-size: 10px;
		font-weight: 600;
		letter-spacing: 0.02em;
		font-variant-numeric: tabular-nums;
	}
	/* Once sort-toggle exists, the +/− button no longer needs margin-left:auto
	   (the sort button is taking that role); neutralize so they stay adjacent. */
	.v4-lane-sort-toggle + .v4-lane-toggle-all { margin-left: 0; }
	.v4-lane-scroll { flex: 1; overflow-y: auto; scrollbar-gutter: stable; position: relative; z-index: 1; }
	.v4-lane-empty { padding: 32px 16px; font-size: 12px; color: #C5C8CD; text-align: center; }

	/* ── Items ── */
	/* Grouped-roots layout — each domain forms a cluster headed by a compact
	   label row (analogous to a schema header in a DB browser). Concepts
	   within the group render at full weight; the group header carries the
	   namespace signal. Borders between groups keep visual separation
	   without the need for extra margin. */
	.v4-group { border-bottom: 1px solid #D8DBE2; }
	.v4-group:last-child { border-bottom: none; }
	.v4-group-collapsed { border-bottom: 1px solid #D8DBE2; }
	/* Group header is a button — reset default button styles so it reads
	   as a row, not as a clickable chip. */
	.v4-group-header {
		display: flex;
		align-items: baseline;
		gap: 8px;
		width: 100%;
		padding: 12px 14px 8px;
		background: #ECEEF3;
		border: none;
		border-radius: 0;
		font: inherit;
		text-align: left;
		cursor: pointer;
		position: sticky;
		top: 0;
		z-index: 1;
		transition: background 80ms ease-out;
	}
	.v4-group-header:hover { background: #E2E6EC; }
	.v4-group-chevron {
		font-size: 11px;
		color: #9CA3AF;
		width: 11px;
		flex-shrink: 0;
		transition: color 80ms ease-out;
	}
	.v4-group-header:hover .v4-group-chevron { color: #4B5563; }
	.v4-group-label {
		flex: 1;
		min-width: 0;
		font-size: 14px;
		font-weight: 600;
		letter-spacing: 0;
		text-transform: none;
		color: #111827;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.v4-group-count {
		flex-shrink: 0;
		font-size: 11px;
		font-weight: 500;
		color: #9CA3AF;
		font-variant-numeric: tabular-nums;
	}

	.v4-item { display: flex; align-items: center; gap: 6px; padding: 10px 12px; cursor: pointer; transition: background 80ms ease-out; border-bottom: 1px solid transparent; }
	.v4-item:hover { background: #E8EBF0; }
	.v4-item-selected { background: #E2E6EC; }
	.v4-item-selected:hover { background: #E2E6EC; }
	.v4-item-label { flex: 1; min-width: 0; font-size: 13px; font-weight: 500; color: #374151; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.v4-item-selected .v4-item-label { color: #111827; font-weight: 600; }
	.v4-item-count { flex-shrink: 0; font-size: 11px; font-weight: 500; color: #9CA3AF; min-width: 14px; text-align: right; padding-right: 6px; }

	/* Roots-lane size signal — three vertical bars rising in height. Filled
	   bars indicate bucket: 1 = small (<10 descendants), 2 = medium (<50),
	   3 = large (50+). Replaces the bare descendant-count badge. */
	/* Size icon — horizontal lines stacked top→bottom inside a fixed-height
	   box. justify-content: space-between pins the three potential positions
	   to top / middle / bottom. Off-state is transparent so a small node
	   shows a single line at the top, medium shows top + middle, large
	   shows all three. Middle line is half-length to read like body text
	   (full / partial / full lines), not three identical bars. */
	.ct-size-bars { display: inline-flex; flex-direction: column; justify-content: space-between; align-items: stretch; flex-shrink: 0; padding: 1px 8px 1px 4px; width: 25px; height: 13px; box-sizing: border-box; }
	.ct-size-bar { height: 1.5px; background: transparent; border-radius: 1px; transition: background 100ms ease-out; }
	.ct-size-bar:nth-child(2) { width: 50%; align-self: flex-start; }
	.ct-size-bar-on { background: #D1D5DB; }
	.v4-item:hover .ct-size-bar-on,
	.v4-item-selected .ct-size-bar-on { background: #9CA3AF; }

	/* Roots-lane age signal — far right, monospace, gray. Compressed format
	   (0d-31d, 1m-12m, Xy) keeps the column tight regardless of tree age. */
	.v4-item-age {
		flex-shrink: 0;
		font-family: 'SF Mono', 'Fira Code', Menlo, monospace;
		font-size: 10px;
		color: #9CA3AF;
		min-width: 30px;
		text-align: right;
		padding-right: 2px;
		font-variant-numeric: tabular-nums;
	}
	.v4-item-selected .v4-item-age { color: #6B7280; }
	.v4-item-orphan .v4-item-age { color: #C0C4CC; }

	/* Orientation styles now live in components/OrientationBlock.svelte. */

	/* ── ADRASI System group ──
	   Pen-blue (ink) treatment for the immutable system-doc row. Sits at the
	   top of the roots lane, reads like any other concept cluster but is
	   tonally distinct so the eye registers it as foundational. Schema header
	   is bolder and a half-shade darker than item rows. */
	.v4-group-system .v4-group-label { color: #1e3a8a; font-weight: 700; }
	.v4-group-system .v4-group-chevron { color: #1e3a8a; }
	.v4-group-system .v4-group-header:hover .v4-group-chevron { color: #1e3a8a; }
	.v4-group-system .v4-group-count { color: #4b6ab5; }
	.v4-group-system .v4-item-label { color: #1e3a8a; }
	.v4-group-system .v4-item-selected .v4-item-label { color: #0f1e4d; }

	/* ── Origin provenance coloring ──
	   Human-initiated (pipeline-v*) = subtle warm gray backdrop.
	   Agent-initiated (mcp / gardener / meld-legacy) = subtle cool gray backdrop.
	   Intentionally near-imperceptible — a gestalt hint, not a loud classifier. */
	/* Root lane no longer colours by provenance — topics stay neutral.
	   Orphan branches (roots below the trunk threshold) render lighter so they
	   read as secondary entries at the bottom of the list. */
	.v4-item-orphan .v4-item-label { color: #9CA3AF; font-weight: 400; }
	.v4-item-orphan .ct-generality { opacity: 0.55; }
	.v4-item-orphan .v4-item-count { color: #B5B9C1; }
	.v4-item-orphan:not(.v4-item-selected) { background: #FAFBFC; }

	/* Dark pen blue when a descendant's last writer is an agent. Applied to
	   concept headings + TOC rows + leaf heads in the middle lane. Nothing
	   else changes — background stays neutral, typography stays the same,
	   only the concept name takes the hue. */
	.ct-agent-last,
	.ct-agent-last .ct-child-concept,
	.ct-agent-last .ct-leaf-concept { color: #1B3A5C; }

	/* Legacy provenance classes — no longer applied to root-lane items.
	   Kept in case downstream rules reference them. */
	.ct-origin-human { background: #FBFAF6; }
	.ct-origin-agent { background: #F6F7FB; }
	.ct-origin-system { background: #F7F7F6; }

	.ct-origin-human.v4-item-selected { background: #E4E1D7; }
	.ct-origin-agent.v4-item-selected { background: #DEE2EA; }
	.ct-origin-system.v4-item-selected { background: #E1E1DF; }

	.ct-origin-human .v4-item-label { color: #1A1A1A; }
	.ct-origin-agent .v4-item-label { color: #1B3A5C; }
	.ct-origin-agent.v4-item-selected .v4-item-label { color: #122B44; }
	.ct-origin-system .v4-item-label { color: #3D3D3D; }
	.ct-origin-system.v4-item-selected .v4-item-label { color: #1F1F1F; }

	.ct-origin-badge { font-size: 8px; font-weight: 700; color: #FFFFFF; background: #1B3A5C; padding: 1px 5px; border-radius: 3px; text-transform: uppercase; letter-spacing: 0.06em; }

	/* ── Generality badges ── */
	.ct-generality { flex-shrink: 0; font-size: 10px; font-weight: 600; font-variant-numeric: tabular-nums; min-width: 28px; text-align: right; }
	.ct-tier-trunk { color: #6B7280; }
	.ct-tier-branch { color: #9CA3AF; }
	.ct-tier-leaf { color: #C4C9D4; }
	.ct-generality-large { font-size: 14px; font-weight: 700; font-variant-numeric: tabular-nums; }
	.ct-tier-label { font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9CA3AF; }

	/* ── Domain tags ── */
	.ct-domain-tags { display: flex; gap: 4px; margin-left: 8px; }
	.ct-domain-tag { font-size: 9px; font-weight: 500; color: #6B7280; background: #ECEEF3; padding: 1px 6px; border-radius: 3px; }

	/* ── Detail meta ── */
	.ct-detail-meta { display: flex; align-items: center; gap: 8px; padding: 12px 14px 8px; }
	.ct-updated-at { font-size: 10.5px; font-weight: 500; letter-spacing: 0.02em; color: #9CA3AF; padding: 0 14px 8px; }

	/* ── Content blocks ── */
	.ct-content-block { padding: 8px 14px; border-bottom: 1px solid #F3F4F6; }
	.ct-content-domain { font-size: 9px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
	.ct-content-text { font-size: 12px; color: #374151; line-height: 1.7; }
	/* Markdown rendered into content-text — match the typographic palette
	   of ConceptFull.cf-content-text but a half-step smaller. Same scope. */
	.ct-content-text :global(p) { margin: 0 0 6px; }
	.ct-content-text :global(p:last-child) { margin-bottom: 0; }
	.ct-content-text :global(strong) { font-weight: 600; color: #1F2937; }
	.ct-content-text :global(em) { font-style: italic; color: #1F2937; }
	.ct-content-text :global(code) { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; background: #F3F4F6; padding: 1px 4px; border-radius: 3px; color: #1F2937; }
	.ct-content-text :global(pre) { background: #F3F4F6; padding: 8px 10px; border-radius: 4px; overflow-x: auto; font-size: 11px; margin: 6px 0; }
	.ct-content-text :global(pre code) { background: transparent; padding: 0; }
	.ct-content-text :global(ul), .ct-content-text :global(ol) { margin: 4px 0 6px; padding-left: 20px; }
	.ct-content-text :global(li) { margin: 1px 0; }
	.ct-content-text :global(h1), .ct-content-text :global(h2), .ct-content-text :global(h3) { font-size: 12px; font-weight: 600; color: #1F2937; margin: 8px 0 3px; }
	.ct-content-text :global(blockquote) { border-left: 2px solid #E5E7EB; margin: 4px 0; padding: 0 0 0 8px; color: #4B5563; font-style: italic; }
	.ct-content-text :global(a) { color: #1B3A5C; text-decoration: underline; }
	/* Markdown tables — 1px charcoal grid. Markdown renders bare <table> with
	   no class, so style via :global() on element selectors. */
	.ct-content-text :global(table) { border-collapse: collapse; margin: 6px 0; font-size: 11.5px; }
	.ct-content-text :global(th),
	.ct-content-text :global(td) { border: 1px solid #6B7280; padding: 4px 8px; text-align: left; vertical-align: top; }
	.ct-content-text :global(th) { background: #F3F4F6; font-weight: 600; color: #1F2937; }

	/* ── Preview text ── */
	.v4-preview-text { font-size: 12px; font-weight: 400; line-height: 1.75; color: #6B7280; margin: 0; padding: 14px 14px 12px; border-bottom: 1px solid #F3F4F6; }
	.v4-preview-lead { font-weight: 600; color: #374151; }
	.v4-preview-sections { padding: 10px 0 8px; }
	.v4-preview-sections-label { font-size: 10px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase; color: #9CA3AF; padding: 0 14px 6px; }

	/* ── Children/source/entity items ── */
	.ct-child-item { display: flex; align-items: center; gap: 8px; padding: 4px 14px; cursor: pointer; transition: background 80ms; }
	.ct-child-item:hover { background: #E8EBF0; }
	.ct-child-concept { font-size: 12px; color: #4B5563; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.ct-source-item { font-size: 11px; color: #6B7280; padding: 2px 14px; }
	.ct-entities { display: flex; flex-wrap: wrap; gap: 4px; padding: 10px 14px; }
	.ct-entity-tag { font-size: 10px; color: #6B7280; background: #F3F4F6; padding: 2px 8px; border-radius: 10px; }

	/* ── Diagram (inline Mermaid) ── */
	.ct-diagram { padding: 24px 14px 20px; }
	.ct-diagram .mermaid { display: flex; justify-content: center; }
	/* Cap diagram display size so Mermaid's useMaxWidth flag doesn't stretch
	   the SVG across the full lane. 520px keeps it feeling like an embedded
	   figure rather than a banner; centers via margin auto. */
	.ct-diagram :global(svg) { max-width: 520px; width: 100%; height: auto; margin: 0 auto; display: block; }
	/* Mermaid output can carry per-class font-size overrides from the
	   :::concept / :::action / etc. classDefs. Neutralize them so the
	   global 12px initialize() setting wins, matching body-text size. */
	.ct-diagram :global(svg text),
	.ct-diagram :global(svg .nodeLabel),
	.ct-diagram :global(svg .label) { font-size: 10.5px !important; }

	/* ── Continuous detail sections (Lane 2) ── */
	.ct-lane-content { background: #F5F6FA; }
	.ct-section { padding: 0; }
	.ct-section-child { border-top: 1px solid #ECEEF3; padding-bottom: 4px; }
	.ct-section-heading { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #374151; padding: 14px 14px 0; }
	.ct-concept-subtitle { font-size: 11px; font-weight: 400; color: #9CA3AF; }

	/* ── Stacked leaf rows under a branch section ── */
	.ct-leaf-row { display: flex; flex-direction: column; gap: 3px; padding: 7px 14px; border-top: 1px solid #F3F4F6; }
	.ct-leaf-row:hover { background: #ECEEF3; }
	.ct-leaf-head { display: flex; align-items: baseline; gap: 8px; }
	.ct-leaf-row .ct-generality { font-size: 9px; flex-shrink: 0; }
	.ct-leaf-concept { font-size: 12px; font-weight: 600; color: #374151; word-break: break-word; }
	.ct-leaf-value { font-size: 11.5px; line-height: 1.55; color: #6B7280; padding-left: 36px; word-break: break-word; white-space: pre-wrap; }

	/* ── Outline lane (Lane 3) ── */
	/* Width is owned by the grid template on .v4-columns — see the
	   .v4-columns-outline-collapsed rule above. The lane itself just needs
	   to handle overflow when the track shrinks under it. */
	.ct-lane-outline { min-width: 0; }

	/* Lane 3 collapsed-rail: vertical bar with chevron + rotated label.
	   Click anywhere on the rail expands the lane. The grid track shrinks
	   to 28px in lockstep so Lane 2 reclaims the freed space. */
	.ct-lane-outline-collapsed {
		background: #F9FAFB;
		border-left: 1px solid #E5E7EB;
		overflow: hidden;
	}
	.ct-lane-rail {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: flex-start;
		gap: 12px;
		width: 100%;
		height: 100%;
		padding: 14px 0;
		background: transparent;
		border: none;
		cursor: pointer;
		transition: background 100ms ease-out;
	}
	.ct-lane-rail:hover { background: #F3F4F6; }
	.ct-lane-rail-chevron {
		font-size: 14px;
		color: #9CA3AF;
		line-height: 1;
		transition: color 100ms ease-out;
	}
	.ct-lane-rail:hover .ct-lane-rail-chevron { color: #374151; }
	.ct-lane-rail-label {
		writing-mode: vertical-rl;
		font-size: 10px;
		font-weight: 500;
		color: #6B7280;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		user-select: none;
		white-space: nowrap;
	}
	.ct-lane-collapse-btn { margin-left: 4px; }
	.ct-lane-outline .v4-lane-header { font-size: 9px; }
	.ct-lane-outline .ct-outline-item { display: flex; align-items: baseline; gap: 5px; padding: 5px 8px; cursor: pointer; transition: background 80ms; }
	.ct-lane-outline .ct-outline-item:hover { background: #E8EBF0; }
	.ct-lane-outline .ct-outline-label { font-size: 10px; color: #6B7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.ct-lane-outline .ct-generality { font-size: 9px; min-width: 22px; }
	.ct-lane-outline .ct-outline-leaf { padding-left: 18px; }
	.ct-lane-outline .ct-outline-leaf .ct-outline-label { color: #8A90A0; }

	.ct-outline-item { display: flex; align-items: center; gap: 6px; padding: 5px 8px; cursor: pointer; transition: background 80ms; }
	.ct-outline-item:hover { background: #ECEEF3; }

	/* ════════════════════════════════════════════════════════════════════
	   MODAL
	   ════════════════════════════════════════════════════════════════════ */

	.v4-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.24); backdrop-filter: blur(2px); display: flex; align-items: flex-start; justify-content: center; padding: 56px 24px; z-index: 1000; overflow-y: auto; }
	.v4-modal { background: #FFFFFF; border-radius: 10px; box-shadow: 0 8px 40px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.04); width: 70vw; max-width: 900px; max-height: 80vh; display: flex; flex-direction: column; }
	.v4-modal-head { display: flex; align-items: center; justify-content: space-between; padding: 18px 20px 14px; background: #F8F9FA; border-bottom: 1px solid #E5E7EB; border-radius: 10px 10px 0 0; flex-shrink: 0; }
	.v4-modal-head-left { display: flex; align-items: baseline; gap: 10px; min-width: 0; flex: 1; }
	.v4-modal-head-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
	.v4-modal-level { font-size: 10px; font-weight: 600; color: #C5C8CD; text-transform: uppercase; letter-spacing: 0.05em; flex-shrink: 0; }
	.v4-modal-title { font-size: 15px; font-weight: 600; color: #1A1A1A; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.v4-modal-close { display: flex; align-items: center; justify-content: center; width: 26px; height: 26px; background: none; border: none; color: #4B5563; cursor: pointer; border-radius: 6px; }
	.v4-modal-close:hover { background: #E5E7EB; color: #374151; }
	.v4-modal-btn { display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; background: none; border: none; color: #4B5563; cursor: pointer; border-radius: 6px; transition: color 120ms ease-out, background 120ms ease-out; }
	.v4-modal-btn:hover { background: #E5E7EB; color: #374151; }
	.v4-modal-btn-active { background: #E5E7EB; color: #374151; }
	.v4-modal-body { flex: 1; overflow-y: auto; padding: 20px; font-size: 13px; color: #374151; line-height: 1.65; }
	.v4-meta-panel { padding: 10px 20px; background: #F8F9FA; border-bottom: 1px solid #E5E7EB; }
	.v4-meta-row { display: flex; align-items: baseline; gap: 12px; padding: 3px 0; }
	.v4-meta-label { font-size: 10px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.04em; min-width: 80px; flex-shrink: 0; }
	.v4-meta-value { font-size: 12px; color: #4B5563; word-break: break-all; }

	/* ── Modal tree-specific ── */
	.ct-modal-breadcrumb { display: flex; align-items: center; gap: 4px; margin-bottom: 12px; flex-wrap: wrap; }
	.ct-modal-crumb { font-size: 11px; color: #9CA3AF; }
	.ct-modal-crumb-sep { font-size: 11px; color: #D1D5DB; }
	.ct-modal-crumb-current { font-size: 11px; color: #374151; font-weight: 600; }
	.ct-modal-context { font-size: 13px; color: #374151; line-height: 1.7; margin: 0 0 16px; }
	.ct-modal-domain-block { margin-bottom: 14px; padding-bottom: 14px; border-bottom: 1px solid #F3F4F6; }
	.ct-modal-domain-label { font-size: 9px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; }
	.ct-modal-domain-text { font-size: 12.5px; color: #4B5563; line-height: 1.7; white-space: pre-wrap; }
	.ct-modal-children { margin-top: 16px; padding-top: 12px; border-top: 1px solid #E5E7EB; }
	.ct-modal-children-label { font-size: 10px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px; }
	.ct-modal-child { display: flex; align-items: center; gap: 8px; padding: 4px 0; cursor: pointer; font-size: 12.5px; color: #4B5563; transition: color 80ms; }
	.ct-modal-child:hover { color: #111827; }

	/* ── Mobile-only elements: hidden on desktop ── */
	.v4-subnav-mobile { display: none; }
	.v4-mobile-tabs { display: none; }
	.v4-filter-drawer { display: none; }

	/* Search clear as button — reset browser button styles */
	button.v4-search-clear { background: none; border: none; padding: 0; line-height: 1; }

	/* ══════════════════════════════════════════════════════════════════
	   MOBILE — max-width: 768px
	   One lane at a time, collapsed subnav, larger touch targets.
	   ══════════════════════════════════════════════════════════════════ */
	@media (max-width: 768px) {

		/* ── Hide desktop subnav, show mobile subnav ── */
		.v4-subnav { display: none; }
		.v4-subnav-mobile {
			display: flex;
			align-items: center;
			gap: 8px;
			height: 44px;
			padding: 0 12px;
			background: #FFFFFF;
			border-bottom: 1px solid #D8DBE2;
			flex-shrink: 0;
		}
		.v4-search-wrap-mobile { flex: 1; }
		.v4-search-wrap-mobile .v4-search-input { width: 100%; height: 32px; font-size: 14px; }
		.v4-filter-toggle {
			flex-shrink: 0;
			font-size: 11px;
			font-weight: 600;
			color: #6B7280;
			background: #F1F3F7;
			border: none;
			border-radius: 5px;
			padding: 6px 12px;
			cursor: pointer;
			letter-spacing: 0.02em;
			transition: all 120ms ease-out;
			min-height: 32px;
		}
		.v4-filter-toggle-active { color: #1A1A1A; background: #E2E6EC; }

		/* ── Filter drawer ── */
		.v4-filter-drawer {
			display: flex;
			flex-direction: column;
			gap: 12px;
			padding: 12px;
			background: #F8F9FA;
			border-bottom: 1px solid #D8DBE2;
			flex-shrink: 0;
		}
		.v4-filter-drawer .v4-type-pills { align-self: flex-start; }
		.v4-filter-drawer .v4-type-pill { font-size: 11px; padding: 5px 12px; min-height: 32px; }
		.v4-filter-drawer .v4-filter { gap: 10px; }
		.v4-filter-drawer .v4-filter-slider { flex: 1; width: auto; height: 4px; }
		.v4-filter-drawer .v4-filter-slider::-webkit-slider-thumb { width: 18px; height: 18px; }
		.v4-filter-drawer .v4-filter-slider::-moz-range-thumb { width: 18px; height: 18px; }

		/* ── Mobile lane tab strip ── */
		.v4-mobile-tabs {
			display: flex;
			background: #ECEEF3;
			border-bottom: 1px solid #D8DBE2;
			flex-shrink: 0;
		}
		.v4-mobile-tab {
			flex: 1;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 5px;
			height: 40px;
			font-size: 12px;
			font-weight: 600;
			color: #9CA3AF;
			background: none;
			border: none;
			border-bottom: 2px solid transparent;
			cursor: pointer;
			transition: all 150ms ease-out;
			letter-spacing: 0.02em;
		}
		.v4-mobile-tab-active {
			color: #1A1A1A;
			border-bottom-color: #374151;
			background: #F5F6FA;
		}
		.v4-mobile-tab-count {
			font-size: 10px;
			font-weight: 500;
			color: #9CA3AF;
			font-variant-numeric: tabular-nums;
		}
		.v4-mobile-tab-active .v4-mobile-tab-count { color: #6B7280; }

		/* ── Columns: single-column, active lane fills space ── */
		.v4-columns {
			grid-template-columns: 1fr;
			grid-template-rows: 1fr;
		}

		/* Mobile lane visibility: only active lane is displayed */
		.v4-mobile-lane-hidden { display: none !important; }

		/* Each visible lane should fill the grid cell fully */
		.v4-lane { border-right: none; }
		.v4-lane:first-child { background: #F5F6FA; }

		/* Hide lane 1 and 3 headers on mobile — the tab strip replaces them.
		   Lane 2 header stays visible: shows concept title + overflow menu + prune controls. */
		.v4-lane > .v4-lane-header { display: none; }
		.ct-lane-content > .v4-lane-header { display: flex; height: 36px; font-size: 12px; }

		/* ── Touch-friendly list items ── */
		.v4-item { padding: 12px 14px; gap: 8px; min-height: 44px; }
		.v4-item-label { font-size: 14px; }
		.v4-item-count { font-size: 12px; }

		/* ── Detail meta: wrap on narrow screens ── */
		.ct-detail-meta { flex-wrap: wrap; padding: 14px 14px 10px; }
		.ct-domain-tags { margin-left: 0; margin-top: 4px; width: 100%; }

		/* ── Content text: slightly larger for readability ── */
		.ct-content-text { font-size: 13px; line-height: 1.75; word-break: break-word; }
		.v4-preview-text { font-size: 13px; line-height: 1.8; }

		/* ── Section headings ── */
		.ct-section-heading { font-size: 14px; padding: 16px 14px 4px; flex-wrap: wrap; }

		/* ── Leaf rows: larger touch targets ── */
		.ct-leaf-row { padding: 10px 14px; }
		.ct-leaf-concept { font-size: 13px; }
		.ct-leaf-value { font-size: 12px; padding-left: 36px; }

		/* ── Children TOC items: touch-friendly ── */
		.ct-child-item { padding: 8px 14px; min-height: 40px; }
		.ct-child-concept { font-size: 13px; }

		/* ── Outline lane items on mobile (when that tab is active) ── */
		.ct-lane-outline .ct-outline-item { padding: 10px 14px; min-height: 40px; }
		.ct-lane-outline .ct-outline-label { font-size: 12px; }
		.ct-lane-outline .ct-outline-leaf { padding-left: 28px; }

		/* ── Overflow menu button: larger tap target ── */
		.ct-overflow-btn { padding: 8px; min-width: 40px; min-height: 40px; }

		/* ── Prune confirm: larger touch target ── */
		.ct-lane-action { min-height: 36px; padding: 6px 12px; font-size: 12px; }

		/* ── Diagrams: prevent overflow ── */
		.ct-diagram { padding: 16px 10px; overflow-x: auto; }

		/* ── Modal ── */
		.v4-overlay { padding: 24px 8px; }
		.v4-modal { width: calc(100vw - 16px); max-width: none; max-height: calc(100vh - 48px); border-radius: 8px; }
		.v4-modal-head { padding: 14px 14px 10px; }
		.v4-modal-title { font-size: 14px; }
		.v4-modal-body { padding: 14px; }
		.v4-modal-close { width: 36px; height: 36px; }
		.v4-modal-btn { width: 36px; height: 36px; }
	}
</style>
